import { Text } from "@mantine/core"
import BlackTextInput from "app/views/components/inputs/BlackTextInput"
import { ReactComponent as InfoIcon } from 'app/styles/info.svg'
import { useMemo } from "react"
import { LoanParams } from "app/models/loan-params"

type MergeLoansPaydownInfoMessageProps = {
    selectedLoans?: LoanParams[]
    newLoanAmount?: string  // For single merge type (merge into single loans)
    newLoanAmounts?: string[]  // For multiple merge type (merge into multiple loans)
    isMultipleMode?: boolean  // For styling context - true if merging into multiple loans, placement of this component will be different
}

export default function MergeLoansPaydownInfoMessage({
    selectedLoans,
    newLoanAmount,
    newLoanAmounts,
    isMultipleMode = false
}: MergeLoansPaydownInfoMessageProps) {

    // Calculate loan principal difference: sum of selected loans minus new loan amount(s)
    const loanPrincipalDifference = useMemo(() => {
        if (!selectedLoans || selectedLoans.length === 0) {
            return 0
        }

        // Sum all selected loan amounts
        const totalSelectedAmount = selectedLoans.reduce((sum, selectedLoan) => {
            const loanAmount = parseFloat(String(selectedLoan.amount)) || 0
            return sum + loanAmount
        }, 0)

        // Calculate total new loan amount(s)
        let totalNewAmount = 0
        if (newLoanAmounts) {
            // Multiple merge type - sum all new loan amounts
            totalNewAmount = newLoanAmounts.reduce((sum, amount) => {
                return sum + (parseFloat(amount) || 0)
            }, 0)
        } else {
            // Single merge type - use single amount
            totalNewAmount = parseFloat(newLoanAmount || '') || 0
        }

        const difference = totalSelectedAmount - totalNewAmount
        return difference
    }, [selectedLoans, newLoanAmount, newLoanAmounts])

    // Check if new loan amount exceeds total selected loans
    const newLoanExceedsOriginal = useMemo(() => {
        return loanPrincipalDifference < 0
    }, [loanPrincipalDifference])

    // Determine the appropriate label based on whether new loan exceeds original
    const inputLabel = useMemo(() => {
        return newLoanExceedsOriginal ? "Incremental New Loan Amount" : "Paydown Amount"
    }, [newLoanExceedsOriginal])

    // Format amount for display (always show absolute value with commas and 2 decimal places)
    const formattedAmount = useMemo(() => {
        const absoluteAmount = Math.abs(loanPrincipalDifference)
        if (absoluteAmount === 0) {
            return '0.00'
        }
        return absoluteAmount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
    }, [loanPrincipalDifference])

    return (
        <div className={`merge-loan-paydown-wrapper${isMultipleMode ? ' merge-loan-paydown-wrapper-multiple' : ''}`}>
            <div className="merge-loans-paydown-row">
                <BlackTextInput
                    label={inputLabel}
                    readOnly={true}
                    value={formattedAmount}
                    onChange={() => { }}
                    styles={{
                        root: {
                            width: '100%'
                        },
                        label: {
                            padding: '10px 0px'
                        }
                    }}
                />
            </div>
            <div className="merge-loans-paydown-info-message">
                <InfoIcon />
                <Text>
                    {newLoanExceedsOriginal
                        ? "The new amount is greater than the original loans"
                        : loanPrincipalDifference > 0
                            ? "The new amount is less than original loans"
                            : "The new amount is equal to the original loans"
                    }
                </Text>
            </div>
        </div>
    )
}
